import { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { LineChart, Line, ResponsiveContainer } from "recharts";
import { Info, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const CATEGORY_STYLE = {
  E: { bg: "bg-salnes-emerald/10", text: "text-salnes-emerald", border: "border-salnes-emerald/20" },
  S: { bg: "bg-salnes-atlantic/10", text: "text-salnes-atlantic", border: "border-salnes-atlantic/20" },
  G: { bg: "bg-salnes-coral/10", text: "text-salnes-coral", border: "border-salnes-coral/20" },
};

export default function EsgKpiCard({ indicator, value, i18nLang, index = 0 }) {
  const { t, i18n } = useTranslation();
  const lang = i18nLang || i18n.language || "gl";
  const style = CATEGORY_STYLE[indicator.category] || CATEGORY_STYLE.G;
  const name = indicator[`name_${lang}`] || indicator.name_gl;
  const desc = indicator[`description_${lang}`] || indicator.description_gl;
  const formula = indicator[`formula_${lang}`] || indicator.formula_gl;

  const trend = value?.trend || [];
  const hasTrend = trend.length >= 2;
  const first = hasTrend ? trend[0].value : 0;
  const last = hasTrend ? trend[trend.length - 1].value : 0;
  const delta = last - first;
  const deltaPct = first ? ((delta / first) * 100).toFixed(1) : null;

  const displayValue = value?.value == null ? "—" : formatNumber(value.value);
  const blocked = value?.blocked === true;
  const provisional = value?.provisional === true && !blocked;
  const blockedReason =
    (value && value[`blocked_reason_${lang}`]) || value?.blocked_reason_es || null;
  const provisionalReason =
    (value && value[`provisional_reason_${lang}`]) ||
    value?.provisional_reason_es || null;
  const simRange = value?.simulated_range;

  return (
    <motion.article
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.05 }}
      data-testid={`esg-card-${indicator.code}`}
      className="group rounded-2xl border hairline bg-white p-5 flex flex-col justify-between min-h-[220px]"
    >
      <div className="flex items-start justify-between gap-3">
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-[0.16em] ${style.bg} ${style.text} ${style.border}`}
        >
          {t(`fase2.category_${indicator.category}`)}
        </span>
        <Dialog>
          <DialogTrigger asChild>
            <button
              className="text-salnes-stone hover:text-salnes-atlantic transition-colors"
              data-testid={`esg-info-${indicator.code}`}
              type="button"
              aria-label={t("fase2.traceability")}
            >
              <Info size={14} />
            </button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl">{name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 text-sm text-salnes-ink/85">
              <div>{desc}</div>
              <div className="border-t hairline pt-3">
                <div className="text-[10px] uppercase tracking-[0.18em] text-salnes-stone mb-1">
                  {t("fase2.formula")}
                </div>
                <code className="text-xs bg-salnes-sand/40 px-2 py-1 rounded block">
                  {formula}
                </code>
              </div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-salnes-stone">
                {t("fase2.data_source")}: {indicator.source_type}
              </div>
              {value?.provisional && (
                <div className="text-xs px-3 py-2 rounded bg-amber-50 border border-amber-200 text-amber-900">
                  {t("fase2.provisional")} · {t("fase2.empty_state_sub")}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-4 flex items-baseline gap-2">
        <div className="font-serif text-4xl text-salnes-ink tabular-nums leading-none">
          {displayValue}
        </div>
        <div className="text-xs text-salnes-stone">{indicator.unit}</div>
      </div>
      <div className="mt-1 text-xs text-salnes-ink/70 line-clamp-2">{name}</div>

      <div className="mt-4 flex items-center justify-between gap-2">
        <div className="h-8 flex-1">
          {hasTrend ? (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trend}>
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke="#0B4C7A"
                  strokeWidth={1.5}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full flex items-center text-[11px] text-salnes-stone">
              —
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 text-[11px]">
          {delta > 0 ? (
            <TrendingUp size={13} className="text-salnes-emerald" />
          ) : delta < 0 ? (
            <TrendingDown size={13} className="text-salnes-coral" />
          ) : (
            <Minus size={13} className="text-salnes-stone" />
          )}
          <span className="tabular-nums text-salnes-stone">
            {deltaPct == null ? "—" : `${deltaPct}%`}
          </span>
        </div>
      </div>
      {blocked ? (
        <div
          className="mt-2 text-[10px] uppercase tracking-[0.14em] text-slate-600 flex items-center gap-1.5"
          data-testid={`esg-blocked-${indicator.code}`}
          title={blockedReason || ""}
        >
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-slate-400" />
          {blockedReason || t("fase2.provisional")}
        </div>
      ) : provisional ? (
        <div
          className="mt-2 text-[10px] uppercase tracking-[0.14em] text-amber-800 flex items-center gap-1.5 group/prov"
          data-testid={`esg-state-${indicator.code}-provisional`}
          title={provisionalReason || ""}
        >
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500" />
          <span>
            {lang === "es" ? "Estimación" : "Estimación"}
            {simRange ? (
              <span className="ml-1 normal-case tracking-normal text-amber-700/80">
                [{simRange.min}–{simRange.max} {simRange.unit}]
              </span>
            ) : null}
          </span>
        </div>
      ) : value?.provisional && (
        <div className="mt-2 text-[10px] uppercase tracking-[0.14em] text-amber-700">
          {t("fase2.provisional")}
        </div>
      )}
    </motion.article>
  );
}

function formatNumber(n) {
  if (n == null || Number.isNaN(n)) return "—";
  if (Math.abs(n) >= 1000) return n.toLocaleString("es-ES", { maximumFractionDigits: 0 });
  if (Number.isInteger(n)) return String(n);
  return n.toFixed(2);
}
